import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, CalendarDays, ChevronLeft, ChevronRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Reveal, Container } from "@/components/common/Primitives";
import { PageHero, SectionHead, Rows, Kicker, RuleDraw, HGallery, TextLink } from "@/components/common/Editorial";
import { DatePriceCalendar } from "@/components/booking/DatePriceCalendar";
import { RoomCard } from "@/components/rooms/RoomCard";
import { PHONE } from "@/components/layout/Header";
import { useLang } from "@/lib/i18n";
import { useContent, minPrice } from "@/lib/content";
import { useSearch } from "@/lib/search";
import { api } from "@/lib/api";
import { fmtDate, nightsBetween, nightsLabel, nok } from "@/lib/format";
import { buildBookingUrl } from "@/lib/booking";
import { usePageMeta } from "@/lib/seo";
import { FACILITIES, ROOM_COPY } from "./Rooms";

/* ------------------------------------------------------------------ lightbox */

const Lightbox = ({ images, name, idx, setIdx, open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-6xl rounded-lg border-0 bg-fjord p-3 text-white sm:p-5 [&>button]:hidden" data-testid="room-gallery-lightbox">
      <DialogTitle className="sr-only">{name}</DialogTitle>
      <DialogDescription className="sr-only">Galleri</DialogDescription>
      <div className="flex items-center justify-between pb-3">
        <span className="index-num text-white/60 tnum">{String(idx + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}</span>
        <button type="button" onClick={() => onOpenChange(false)} className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-white/20 text-white transition-colors hover:bg-white/10" aria-label="Lukk" data-testid="lightbox-close"><X className="h-5 w-5" strokeWidth={1.5} /></button>
      </div>
      <div className="relative">
        <img src={images[idx]} alt="" className="max-h-[74vh] w-full rounded-photo object-contain" />
        <button type="button" onClick={() => setIdx((idx - 1 + images.length) % images.length)} className="absolute left-3 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-md bg-white text-foreground transition-colors hover:bg-white/90" aria-label="Previous" data-testid="lightbox-prev"><ChevronLeft className="h-5 w-5" strokeWidth={1.5} /></button>
        <button type="button" onClick={() => setIdx((idx + 1) % images.length)} className="absolute right-3 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-md bg-white text-foreground transition-colors hover:bg-white/90" aria-label="Next" data-testid="lightbox-next"><ChevronRight className="h-5 w-5" strokeWidth={1.5} /></button>
      </div>
    </DialogContent>
  </Dialog>
);

/* ------------------------------------------------------------------ small parts */

/** Quiet content block: caps label + hairline, then the body. */
const Block = ({ label, children, testid, className = "" }) => (
  <section className={`border-t border-border pt-6 ${className}`} data-testid={testid}>
    <Kicker>{label}</Kicker>
    <div className="mt-5">{children}</div>
  </section>
);

const fieldLabel = "label-caps block text-muted-foreground";

/* ------------------------------------------------------------------ page */

export default function RoomDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { lang, tx, tr } = useLang();
  const { data, isLoading } = useContent();
  const { search, setSearch } = useSearch();
  const rt = useMemo(() => (data?.room_types || []).find((r) => r.slug === slug), [data, slug]);
  const [guests, setGuests] = useState(2);
  const [board, setBoard] = useState("standard");
  const [calOpen, setCalOpen] = useState(false);
  const [lb, setLb] = useState(false);
  const [idx, setIdx] = useState(0);
  usePageMeta(rt ? tr(rt.name) : tx("Rom", "Room"), rt ? tr(rt.description) : "");
  useEffect(() => {
    if (rt) setGuests(Math.min(Math.max(1, search.adults + search.children), rt.max_guests));
  }, [rt]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasDates = !!(search.ci && search.co);
  const avail = useQuery({
    queryKey: ["availability", search.ci, search.co, guests],
    queryFn: async () => (await api.get("/availability", { params: { check_in: search.ci, check_out: search.co, guests } })).data,
    enabled: hasDates,
  });
  const av = avail.data?.room_types?.find((a) => a.room_type_id === rt?.id);
  const total = av ? (board !== "standard" ? av.board_quotes?.[board]?.subtotal : av.quotes?.[String(guests)]?.subtotal) : null;

  if (isLoading) {
    // Dark placeholder keeps the transparent header legible while the content API answers.
    return (
      <div data-testid="room-detail-loading">
        <div className="h-[78svh] min-h-[560px] max-h-[900px] bg-fjord" data-photo-hero aria-hidden />
        <Container className="py-16"><div className="h-6 w-1/3 animate-pulse rounded-sm bg-muted" /><div className="mt-4 h-6 w-2/3 animate-pulse rounded-sm bg-muted" /></Container>
      </div>
    );
  }
  if (!rt) {
    return (
      <Container className="py-24 lg:py-32" data-testid="room-not-found">
        <Kicker>404</Kicker>
        <h1 className="mt-4 text-[2.5rem] font-medium leading-[1.02] tracking-[-0.03em] text-foreground sm:text-[3.25rem]">{tx("Fann ikkje rommet.", "Room not found.")}</h1>
        <Button asChild size="lg" className="mt-8"><Link to="/rom">{tx("Til alle rom og hytter", "All rooms and cabins")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} /></Link></Button>
      </Container>
    );
  }

  const building = (data?.buildings || []).find((b) => b.id === rt.building);
  const byBuilding = Object.fromEntries((data?.buildings || []).map((b) => [b.id, b]));
  const others = (data?.room_types || []).filter((r) => r.id !== rt.id).slice(0, 3);
  const n = nightsBetween(search.ci, search.co);
  const images = rt.images || [];
  const copy = ROOM_COPY[rt.id] ? (lang === "en" ? ROOM_COPY[rt.id].en : ROOM_COPY[rt.id].nn) : null;
  const body = copy ? copy[0] : tr(rt.description);
  const highlights = copy ? copy[1] : rt.highlights?.[lang] || rt.highlights?.nn || [];
  const from = minPrice(rt);
  const goBook = () => navigate(buildBookingUrl({ ...search, cart: [{ room_type_id: rt.id, guests, board }], step: hasDates ? 3 : 1 }));
  const open = (i) => { setIdx(i); setLb(true); };

  const specs = [
    [tx("Hus", "House"), tr(building?.name)],
    [tx("Gjester", "Guests"), tx(`${rt.base_guests} i standard${rt.max_extra_beds ? ` · inntil ${rt.max_guests} med ekstraseng` : ""}`, `${rt.base_guests} standard${rt.max_extra_beds ? ` · up to ${rt.max_guests} with extra bed` : ""}`)],
    rt.max_extra_beds > 0 ? [tx("Ekstraseng", "Extra bed"), `${nok(rt.extra_bed_price, lang)} ${tx("per seng per natt", "per bed per night")}`] : null,
    [tx("Kjæledyr", "Pets"), rt.dogs_allowed ? tx("Velkomne etter avtale", "Welcome by arrangement") : tx("Ikkje i dette huset – vel sidefløya", "Not in this house – choose the side wing")],
    [tx("Tilkomst", "Access"), rt.step_free ? tx("Utan trapper", "Step-free") : tx("Spør oss om rom utan trapper", "Ask us about step-free rooms")],
    [tx("Frukost", "Breakfast"), tx("Inkludert · servert i Restaurant Baronen", "Included · served in Restaurant Baronen")],
    [tx("Innsjekk / utsjekk", "Check-in / out"), tx("Frå kl. 16.00 / innan kl. 11.00", "From 16:00 / by 11:00")],
  ].filter(Boolean);

  const gallery = images.map((src, i) => ({ src, title: `${tr(rt.name)} ${i + 1}`, tall: i % 3 === 1 }));

  return (
    <div data-testid="room-detail-page" className="pb-24 lg:pb-0">
      <PageHero
        testid="room-hero"
        image={images[0]}
        position="50% 50%"
        alt={tr(rt.name)}
        kicker={`${tr(building?.name) || ""} · ${tx(`Inntil ${rt.max_guests} gjester`, `Up to ${rt.max_guests} guests`)}`}
        title={tr(rt.name)}
        lead={tr(rt.tagline)}
      >
        <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-4">
          <Button size="lg" variant="inverse" onClick={goBook} data-testid="room-hero-book">{tx("Sjå pris og ledige datoar", "See price and available dates")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} /></Button>
          {from && <p className="hero-shadow text-sm text-white/80 tabular">{tx("Frå", "From")} <span className="font-medium text-white">{nok(from, lang)}</span> {tx("per natt · frukost inkludert", "per night · breakfast included")}</p>}
          <Link to="/rom" className="group inline-flex items-center gap-2 text-sm font-medium text-white/80 transition-colors hover:text-white" data-testid="room-back-link"><ArrowLeft className="h-4 w-4 transition-transform duration-200 group-hover:-translate-x-0.5" strokeWidth={1.5} /> {tx("Alle rom og hytter", "All rooms and cabins")}</Link>
        </div>
      </PageHero>

      {/* Body + sticky booking panel */}
      <Container className="grid gap-12 pt-16 lg:grid-cols-12 lg:gap-12 lg:pt-24">
        <div className="min-w-0 lg:col-span-7">
          <Reveal>
            <p className="max-w-[58ch] text-[1.25rem] leading-[1.5] text-foreground sm:text-[1.375rem]" data-testid="room-description">{body}</p>
            {copy && <p className="mt-5 max-w-[60ch] text-[1.0625rem] leading-[1.65] text-muted-foreground">{tr(rt.description)}</p>}
          </Reveal>

          <Reveal className="mt-12">
            <Block label={tx("Det du får", "What you get")} testid="room-highlights-block">
              <ul className="flex flex-wrap gap-x-5 gap-y-2.5" data-testid="room-highlights">
                {highlights.map((h) => <li key={h} className="label-caps text-foreground/75">{h}</li>)}
              </ul>
            </Block>
          </Reveal>

          <Reveal className="mt-12">
            <Block label={tx("Fakta", "Details")} testid="room-specs-block">
              <Rows rows={specs} testid="room-specs" labelWidth="sm:grid-cols-[10rem_1fr]" className="border-t-0" />
            </Block>
          </Reveal>

          <Reveal className="mt-12">
            <Block label={tx("Prisar", "Rates")} testid="room-price-block">
              <div data-testid="room-price-table">
                <div className="grid grid-cols-[1fr_auto_auto] gap-x-8 pb-3 sm:gap-x-14">
                  <span className="label-caps text-muted-foreground">{tx("Per rom per natt", "Per room per night")}</span>
                  <span className="label-caps text-muted-foreground">{tx("Midtveke", "Weekday")}</span>
                  <span className="label-caps text-muted-foreground">{tx("Helg", "Weekend")}</span>
                </div>
                <div className="grid grid-cols-[1fr_auto_auto] gap-x-8 border-t border-border py-4 text-[0.9375rem] sm:gap-x-14">
                  <span className="text-foreground">{tx("1 person", "1 guest")}</span><span className="font-medium tnum text-foreground">{nok(rt.prices.weekday.p1, lang)}</span><span className="font-medium tnum text-foreground">{nok(rt.prices.weekend.p1, lang)}</span>
                </div>
                {rt.max_guests > 1 && (
                  <div className="grid grid-cols-[1fr_auto_auto] gap-x-8 border-t border-border py-4 text-[0.9375rem] sm:gap-x-14">
                    <span className="text-foreground">{tx("2 personar", "2 guests")}</span><span className="font-medium tnum text-foreground">{nok(rt.prices.weekday.p2, lang)}</span><span className="font-medium tnum text-foreground">{nok(rt.prices.weekend.p2, lang)}</span>
                  </div>
                )}
                {(rt.board_options || []).map((bo) => (
                  <div key={bo.id} className="grid grid-cols-[1fr_auto] gap-x-8 border-t border-border py-4 text-[0.9375rem] sm:gap-x-14">
                    <span className="text-foreground">{tr(bo.name)} <span className="text-muted-foreground">· {tx("per person per natt", "per person per night")}</span></span><span className="font-medium tnum text-foreground">{nok(bo.weekday, lang)}</span>
                  </div>
                ))}
                <p className="border-t border-border pt-4 text-sm leading-relaxed text-muted-foreground">{tx("Frukost og mva er inkludert. Helg er fredag og laurdag. Du betaler på hotellet.", "Breakfast and VAT included. Weekend is Friday and Saturday. You pay at the hotel.")}</p>
              </div>
            </Block>
          </Reveal>

          <Reveal className="mt-12">
            <Block label={tx("På rommet", "In the room")} testid="room-facilities-block">
              <ul className="grid gap-x-8 gap-y-3 sm:grid-cols-2" data-testid="room-facilities">
                {FACILITIES.map(({ icon: I, nn, en }) => (
                  <li key={nn} className="flex items-center gap-3 text-[0.9375rem] text-foreground"><I className="h-[18px] w-[18px] shrink-0 text-muted-foreground" strokeWidth={1.5} />{tx(nn, en)}</li>
                ))}
              </ul>
            </Block>
          </Reveal>
        </div>

        {/* sticky booking panel */}
        <aside className="lg:col-span-5 xl:col-span-4 xl:col-start-9">
          <div className="sticky top-28 rounded-lg border border-border bg-card p-6 shadow-float" data-testid="room-booking-widget">
            <div className="flex items-baseline justify-between gap-4">
              <p className="label-caps text-muted-foreground">{tx("Bestill direkte", "Book direct")}</p>
              <p className="text-sm text-muted-foreground tabular">{tx("frå", "from")} <span className="text-[1.125rem] font-medium tnum text-foreground" data-testid="room-widget-from-price">{nok(from, lang)}</span> / {tx("natt", "night")}</p>
            </div>

            <div className="mt-5 divide-y divide-input rounded-md border border-input">
              <Popover open={calOpen} onOpenChange={setCalOpen}>
                <PopoverTrigger asChild>
                  <button type="button" className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-secondary/60" data-testid="room-widget-dates">
                    <CalendarDays className="h-[18px] w-[18px] shrink-0 text-foreground" strokeWidth={1.5} />
                    <span className="min-w-0">
                      <span className={fieldLabel}>{tx("Datoar", "Dates")}</span>
                      <span className={`mt-1 block truncate text-[0.9375rem] font-medium tabular ${hasDates ? "text-foreground" : "text-foreground/55"}`}>{hasDates ? `${fmtDate(search.ci, lang)} – ${fmtDate(search.co, lang)} · ${nightsLabel(n, lang)}` : tx("Vel innsjekk og utsjekk", "Choose check-in and check-out")}</span>
                    </span>
                  </button>
                </PopoverTrigger>
                <PopoverContent align="end" sideOffset={10} className="w-[min(420px,94vw)] rounded-lg border-border bg-card p-5 shadow-pop" data-testid="room-widget-calendar">
                  <DatePriceCalendar ci={search.ci} co={search.co} guests={guests} months={1} onChange={(v) => { setSearch(v); if (v.ci && v.co) setTimeout(() => setCalOpen(false), 200); }} testid="room-widget-price-calendar" />
                </PopoverContent>
              </Popover>
              <div className="grid grid-cols-2 divide-x divide-input">
                <div className="px-4 py-3">
                  <p className={fieldLabel}>{tx("Gjester", "Guests")}</p>
                  <Select value={String(guests)} onValueChange={(v) => setGuests(parseInt(v, 10))}>
                    <SelectTrigger className="mt-1 h-7 border-0 bg-transparent p-0 text-[0.9375rem] font-medium shadow-none focus:ring-0" data-testid="room-widget-guests"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-lg">{Array.from({ length: rt.max_guests }, (_, i) => i + 1).map((g) => <SelectItem key={g} value={String(g)}>{g} {g === 1 ? tx("person", "guest") : tx("personar", "guests")}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="px-4 py-3">
                  <p className={fieldLabel}>{tx("Måltid", "Meals")}</p>
                  {(rt.board_options || []).length > 0 ? (
                    <Select value={board} onValueChange={setBoard}>
                      <SelectTrigger className="mt-1 h-7 border-0 bg-transparent p-0 text-[0.9375rem] font-medium shadow-none focus:ring-0" data-testid="room-widget-board"><SelectValue /></SelectTrigger>
                      <SelectContent className="rounded-lg">
                        <SelectItem value="standard">{tx("Rom og frukost", "Room & breakfast")}</SelectItem>
                        {rt.board_options.map((bo) => <SelectItem key={bo.id} value={bo.id}>{tr(bo.name)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : <p className="mt-1 h-7 text-[0.9375rem] font-medium leading-7 text-foreground">{tx("Frukost inkl.", "Breakfast incl.")}</p>}
                </div>
              </div>
            </div>

            <div className="mt-5 min-h-[3rem]" data-testid="room-widget-price">
              {hasDates ? (
                avail.isLoading ? <p className="text-sm text-muted-foreground">{tx("Sjekkar ledige rom …", "Checking availability …")}</p> : av ? (
                  <>
                    <div className="flex items-baseline justify-between gap-4">
                      <span className="text-sm text-muted-foreground">{tx("Totalt", "Total")} · {nightsLabel(n, lang)}</span>
                      <span className="text-[1.5rem] font-medium tnum tracking-[-0.01em] text-foreground" data-testid="room-widget-total">{nok(total, lang)}</span>
                    </div>
                    <p className={`mt-1 text-sm ${av.available === 0 ? "text-destructive" : av.available <= 3 ? "text-warning" : "text-success"}`} data-testid="room-widget-availability">
                      {av.available === 0 ? tx("Fullt desse datoane – prøv andre datoar", "Sold out for these dates – try other dates") : av.available <= 3 ? tx(`Berre ${av.available} rom att`, `Only ${av.available} left`) : tx("Ledig", "Available")}
                    </p>
                  </>
                ) : <p className="text-sm text-muted-foreground">–</p>
              ) : <p className="text-sm leading-relaxed text-muted-foreground">{tx("Vel datoar for å sjå totalpris og om rommet er ledig.", "Choose dates to see the total and whether the room is available.")}</p>}
            </div>
            <Button size="xl" className="mt-5 w-full" onClick={goBook} disabled={hasDates && av?.available === 0} data-testid="room-widget-book">
              {hasDates ? tx("Bestill dette rommet", "Book this room") : tx("Vel datoar og bestill", "Choose dates and book")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
            </Button>
            <dl className="mt-6 divide-y divide-border border-t border-border text-[0.8125rem]">
              {[
                [tx("Betaling", "Payment"), tx("På hotellet – inga forskotsbetaling", "At the hotel – no prepayment")],
                [tx("Avbestilling", "Cancellation"), tx("Gratis fram til kl. 18.00 dagen før", "Free until 18:00 the day before")],
                [tx("Pris", "Rate"), tx("Beste pris når du bestiller direkte", "Best rate when you book direct")],
              ].map(([k, v]) => (
                <div key={k} className="grid grid-cols-[6rem_1fr] gap-3 py-2.5">
                  <dt className="text-muted-foreground">{k}</dt>
                  <dd className="text-foreground">{v}</dd>
                </div>
              ))}
            </dl>
            <p className="mt-4 text-[0.8125rem] text-muted-foreground">{tx("Vil du heller ringje?", "Would you rather call?")} <a href={PHONE.tel} className="font-medium text-foreground underline decoration-border underline-offset-4 hover:decoration-foreground tabular">{PHONE.display}</a></p>
          </div>
        </aside>
      </Container>

      {/* Gallery */}
      {images.length > 1 && (
        <section className="pt-24 lg:pt-32" data-testid="room-gallery-section">
          <Container>
            <RuleDraw className="mb-16 lg:mb-24" />
            <Reveal><SectionHead kicker={tx("Galleri", "Gallery")} title={tx("Sjå nærare på rommet", "A closer look at the room")} testid="room-gallery-title" /></Reveal>
            <Reveal delay={0.05} className="mt-12 lg:mt-16"><HGallery items={gallery} testid="room-gallery" onItemClick={open} /></Reveal>
          </Container>
        </section>
      )}

      {/* Other rooms */}
      <section className="py-24 lg:py-32" data-testid="room-others">
        <Container>
          <RuleDraw className="mb-16 lg:mb-24" />
          <Reveal><SectionHead kicker={tx("Andre rom", "Other rooms")} title={tx("Kanskje passar eit av desse betre?", "Perhaps one of these suits you better?")} action={tx("Sjå alle rom og hytter", "See all rooms and cabins")} to="/rom" testid="room-others-title" /></Reveal>
          <div className="mt-12 grid gap-x-8 gap-y-14 sm:grid-cols-2 lg:mt-16 lg:grid-cols-3">
            {others.map((o, i) => <Reveal key={o.id} delay={i * 0.05}><RoomCard rt={o} building={byBuilding[o.building]} testid={`room-other-${o.id}`} /></Reveal>)}
          </div>
          <div className="mt-12 border-t border-border pt-6 text-[0.9375rem] text-muted-foreground">
            {tx("Usikker på kva rom du skal velje?", "Not sure which room to choose?")}{" "}
            <TextLink href={PHONE.tel} testid="room-call-link" className="ml-1">{tx("Ring resepsjonen", "Call reception")} {PHONE.display}</TextLink>
          </div>
        </Container>
      </section>

      {/* mobile bottom bar */}
      <div className="no-print fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 px-5 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur-xl lg:hidden" data-testid="room-mobile-bar">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[0.9375rem] font-medium text-foreground tabular">{hasDates && total ? nok(total, lang) : `${tx("frå", "from")} ${nok(from, lang)}`} <span className="text-sm font-normal text-muted-foreground">{hasDates && total ? `· ${nightsLabel(n, lang)}` : `/ ${tx("natt", "night")}`}</span></p>
            <p className="truncate text-sm text-muted-foreground">{hasDates ? `${fmtDate(search.ci, lang, "d. MMM")} – ${fmtDate(search.co, lang, "d. MMM")}` : tx("Frukost inkludert · betal på hotellet", "Breakfast included · pay at the hotel")}</p>
          </div>
          <Button size="lg" className="px-6" onClick={goBook} disabled={hasDates && av?.available === 0} data-testid="room-mobile-book">{tx("Bestill", "Book")}</Button>
        </div>
      </div>

      {images.length > 0 && <Lightbox images={images} name={tr(rt.name)} idx={idx} setIdx={setIdx} open={lb} onOpenChange={setLb} />}
    </div>
  );
}
